import React, { Suspense } from 'react';
import { History } from 'history';
import { useTranslation } from 'react-i18next';
import {
  Prompt,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import MFELoader from 'routes/components/MFELoader';
import { MFE_WC_EXPORTS } from 'shared/constants';
import { useGetAuthData } from 'shared/hooks';
import useEventListener from 'shared/hooks/useEventListener';
import NotFoundPage from 'shared/screens/NotFoundPage';
import { useQueryParams } from 'shared/utils';
import config from 'utils/config';
import { BreadcrumpDataType, HostConfigType } from '../../../shared/types';
// import List from 'surveyserv/SurveyList';
// import Action from 'surveyserv/SurveyAction';
import { Center } from '../../../shared/styles';
import { useAccessControl } from '../../../utils/access-control';
import List, { HostPropType } from './list';

type surveyType = {
  setBreadcrumpData?: (
    str: BreadcrumpDataType | React.SetStateAction<BreadcrumpDataType>,
  ) => void;
  hostConfig: HostConfigType;
};

const Survey = ({ hostConfig, setBreadcrumpData }: surveyType) => {
  const { t } = useTranslation();
  const { role, access } = useAccessControl();
  const { path } = useRouteMatch();
  const history = useHistory();
  const [isDirty] = React.useState<boolean>(false);

  const {
    data: surveyServAuthInfo,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGetAuthData();

  const refreshTokenHandler = () => {
    refetch();
  };

  const routeHandler = (data: any) => {
    if (data.detail.path) history.push(data.detail.path);
  };

  const UpdateBreadcrumb = (data: any) => {
    if (setBreadcrumpData)
      if (typeof data.detail === 'function') {
        setBreadcrumpData((old: BreadcrumpDataType) => data.detail(old));
      } else {
        setBreadcrumpData(data.detail);
      }
  };

  useEventListener('survey_mfe:route', routeHandler);
  useEventListener('survey_mfe:breadcrumb', UpdateBreadcrumb);
  useEventListener('survey_mfe:refresh_token', refreshTokenHandler);

  if (isLoading || isFetching) {
    return <MFELoader />;
  }

  if (isError) return <Center>{t('empty_state.message.network_error')}</Center>;

  return (
    <>
      <Suspense fallback={<MFELoader />}>
        <Prompt
          when={isDirty}
          message={`${t('forms.toast.success.changes_not_saved')}`}
        />
        <Switch>
          <Route exact path={path}>
            <List
              role={role}
              surveyServAuthInfo={surveyServAuthInfo}
              hostConfig={hostConfig}
              product="freshsurvey"
            />
          </Route>
          {access.includes('survey:write') ? (
            <Route exact path={`${path}/create`}>
              <SurveyCreate
                role={role}
                surveyServAuthInfo={surveyServAuthInfo}
                hostConfig={hostConfig}
                product="freshsurvey"
              />
            </Route>
          ) : null}
          <Route exact path={`${path}/:id/edit`}>
            <SurveyEdit
              role={role}
              surveyServAuthInfo={surveyServAuthInfo}
              hostConfig={hostConfig}
              product="freshsurvey"
            />
          </Route>
          <Route path="*">
            <NotFoundPage />
          </Route>
        </Switch>
      </Suspense>
    </>
  );
};

export default Survey;

const SurveyEdit = (hostProps: HostPropType) => {
  const { t } = useTranslation();
  const hostParams: { id: string } = useParams();
  // const { search } = useLocation();
  // const queryParams = new URLSearchParams(search);
  return (
    <survey-components
      url={config.SURVEY_MICROFRONTEND_REMOTE}
      module-name={MFE_WC_EXPORTS.name}
      export-name={MFE_WC_EXPORTS.modules.SurveyBuilderWC}
      fn="default"
      data={JSON.stringify({
        hostParams,
        ...hostProps,
      })}
    >
      <div slot="loading">
        <MFELoader wcLoading />
      </div>
      <p slot="failure">{t('loading_failed')}</p>
    </survey-components>
  );
};

const SurveyCreate = (hostProps: HostPropType) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const { push }: History = useHistory();
  const { pathname, state, search } =
    useLocation<
      Record<string, string | boolean | number | object | undefined>
    >();

  if (state?.isFreddyPage) {
    push(`${pathname}${search}`, undefined);
    return <MFELoader wcLoading />;
  }
  // const hostParams: { id: string } = useParams();
  // const { search } = useLocation();
  // const queryParams = new URLSearchParams(search);
  // console.log('HOSTPARAMS', hostParams);
  return (
    <survey-components
      url={config.SURVEY_MICROFRONTEND_REMOTE}
      module-name={MFE_WC_EXPORTS.name}
      export-name={MFE_WC_EXPORTS.modules.SurveyBuilderWC}
      fn="default"
      data={JSON.stringify({
        ...hostProps,
        hostParams: {
          isImport: Boolean(queryParams.get('import') || false),
        },
      })}
    >
      <div slot="loading">
        <MFELoader wcLoading />
      </div>
      <p slot="failure">{t('loading_failed')}</p>
    </survey-components>
  );
};
